import { useMutation, useQueryClient } from 'react-query';
import Axios from 'axios';

import apiUrls from 'base/api/urls';
import { StandardError } from 'base/api/errors';
import { useDetailsQuery, useListQuery } from 'base/api/hooks';
import { RecipesUnit } from 'base/api/types';
import { composeUrl } from 'common/utils';
import { CommonVars, PK } from 'common/types';

export const useUnitsList = () => {
  return useListQuery<RecipesUnit>(apiUrls.UNITS.LIST);
};

export const useUnitsDetails = (pk?: PK) => {
  return useDetailsQuery<RecipesUnit>({ queryKey: [apiUrls.UNITS.DETAILS, { pk }], pk });
};

interface UnitsCreateVars {
  name_plural: string;
  name_singular: string;
}

export const useUnitsCreate = () => {
  const queryClient = useQueryClient();

  return useMutation<RecipesUnit, StandardError, UnitsCreateVars>(
    (data) => Axios.post(apiUrls.UNITS.LIST, data),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(apiUrls.UNITS.LIST);
        queryClient.setQueryData([apiUrls.UNITS.DETAILS, { pk: data.pk }], data);
      },
    },
  );
};

interface UnitsUpdateVars extends CommonVars, UnitsCreateVars {}

export const useUnitsUpdate = () => {
  const queryClient = useQueryClient();

  return useMutation<RecipesUnit, StandardError, UnitsUpdateVars>(
    ({ pk, ...data }) =>
      Axios.patch(composeUrl(apiUrls.UNITS.DETAILS, { params: { pk } }), data),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(apiUrls.UNITS.LIST);
        queryClient.setQueryData([apiUrls.UNITS.DETAILS, { pk: data.pk }], data);
      },
    },
  );
};

export const useUnitsDelete = () => {
  const queryClient = useQueryClient();

  return useMutation<never, StandardError, CommonVars>(
    ({ pk }) => Axios.delete(composeUrl(apiUrls.UNITS.DETAILS, { params: { pk } })),
    {
      onSuccess: (data, vars) => {
        queryClient.invalidateQueries(apiUrls.UNITS.LIST);
        queryClient.invalidateQueries(
          composeUrl(apiUrls.UNITS.DETAILS, {
            params: { pk: vars.pk },
          }),
        );
      },
    },
  );
};
